import { API_BASE_URL, TOKEN_STORAGE_KEY } from '../config/index.js';
import { tr } from '../i18n/translate.js';

/**
 * Thin fetch wrapper around the backend API.
 *
 * - Attaches the stored Bearer token automatically.
 * - Unwraps the backend's `{ data }` envelope on success.
 * - Throws an Error carrying { status, details } on failure, so callers can
 *   `try/catch` and show err.message.
 */
class ApiService {
  async request(endpoint, { method = 'GET', body, headers } = {}) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);

    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json() : null;

    if (!res.ok) {
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const shown = payload?.error?.message ? tr(message) : tr('Request failed ({status})', { status: res.status });
      // Shown in the user's language when the message is in the dictionary; sourceMessage keeps the English for comparisons.
      const error = new Error(shown);
      error.sourceMessage = message;
      error.status = res.status;
      error.details = payload?.error?.details;
      throw error;
    }

    return payload?.data ?? payload;
  }

  // ---- Auth ----
  register({ fullName, email, propertyId, department, password, invitationToken }) {
    return this.request('/auth/register', {
      method: 'POST',
      body: { fullName, email, propertyId, department, password, ...(invitationToken ? { invitationToken } : {}) },
    });
  }

  login({ email, password }) {
    return this.request('/auth/login', { method: 'POST', body: { email, password } });
  }

  /** Demo one-click sign-in by role: only listed when the server has DEMO_LOGIN on. */
  demoAccounts() {
    return this.request('/auth/demo-accounts');
  }

  demoLogin({ email }) {
    return this.request('/auth/demo-login', { method: 'POST', body: { email } });
  }

  forgotPassword({ email }) {
    return this.request('/auth/forgot-password', { method: 'POST', body: { email } });
  }

  resetPassword({ token, password }) {
    return this.request('/auth/reset-password', { method: 'POST', body: { token, password } });
  }

  getRegistrationOptions(token) {
    return this.request(`/auth/registration-options?token=${encodeURIComponent(token)}`);
  }

  listUserDepartments() {
    return this.request('/users/departments');
  }

  me() {
    return this.request('/auth/me');
  }

  // ---- Localisation ----
  setMyLanguage(language) {
    return this.request('/localisation/me', { method: 'PUT', body: { language } });
  }

  getReportingLanguage() {
    return this.request('/localisation/reporting-language');
  }

  setReportingLanguage(language) {
    return this.request('/localisation/reporting-language', { method: 'PUT', body: { language } });
  }

  listReasonCodes() {
    return this.request('/localisation/reason-codes');
  }

  // ---- Users (User Directory, Admin only) ----
  listUsers({ page = 1, pageSize = 10, search, role, propertyId, status } = {}) {
    const q = new URLSearchParams({ page, pageSize });
    if (search) q.set('search', search);
    if (role) q.set('role', role);
    if (propertyId) q.set('propertyId', propertyId);
    if (status) q.set('status', status);
    return this.request(`/users?${q.toString()}`);
  }

  getUserDirectorySummary() {
    return this.request('/users/summary');
  }

  updateUser(id, body) {
    return this.request(`/users/${id}`, { method: 'PATCH', body });
  }

  setUserRoles(id, roles) {
    return this.request(`/users/${id}/roles`, { method: 'PUT', body: { roles } });
  }

  deactivateUser(id) {
    return this.request(`/users/${id}/deactivate`, { method: 'POST' });
  }

  reactivateUser(id) {
    return this.request(`/users/${id}/reactivate`, { method: 'POST' });
  }

  sendUserPasswordReset(id) {
    return this.request(`/users/${id}/reset-password`, { method: 'POST' });
  }

  inviteUser({ fullName, email, propertyId, department, role }) {
    return this.request('/users/invitations', { method: 'POST', body: { fullName, email, propertyId, department, role } });
  }

  // ---- Roles & Permissions (Admin only) ----
  listRoles() {
    return this.request('/roles');
  }

  getPermissionCatalog() {
    return this.request('/roles/catalog');
  }

  getRolePermissions(role) {
    return this.request(`/roles/${role}/permissions`);
  }

  saveRolePermissions(role, grants) {
    return this.request(`/roles/${role}/permissions`, { method: 'PUT', body: { grants } });
  }

  // ---- Properties & Locations (Admin / Property Manager) ----
  listProperties() {
    return this.request('/properties');
  }

  createProperty(body) {
    return this.request('/properties', { method: 'POST', body });
  }

  updateProperty(id, body) {
    return this.request(`/properties/${id}`, { method: 'PATCH', body });
  }

  getLocationTree(propertyId) {
    return this.request(`/properties/${propertyId}/locations`);
  }

  createLocation(propertyId, body) {
    return this.request(`/properties/${propertyId}/locations`, { method: 'POST', body });
  }

  updateLocation(id, body) {
    return this.request(`/locations/${id}`, { method: 'PATCH', body });
  }

  stampLocation(id) {
    return this.request(`/locations/${id}/stamp`, { method: 'POST' });
  }

  deleteLocation(id) {
    return this.request(`/locations/${id}`, { method: 'DELETE' });
  }

  deactivateLocation(id, body) {
    return this.request(`/locations/${id}/deactivate`, { method: 'POST', body });
  }

  reactivateLocation(id) {
    return this.request(`/locations/${id}/reactivate`, { method: 'POST' });
  }

  importLocations(propertyId, csv) {
    return this.request(`/properties/${propertyId}/locations/import`, { method: 'POST', body: { csv } });
  }

  // Location Types (AOS-KIT-001 / AOS-REG-003), bulk rule (AOS-REG-002), room-list paste, clone (AOS-REG-006)
  listLocationTypes(propertyId) {
    return this.request(`/properties/${propertyId}/location-types`);
  }

  createLocationType(propertyId, body) {
    return this.request(`/properties/${propertyId}/location-types`, { method: 'POST', body });
  }

  updateLocationType(propertyId, typeId, body) {
    return this.request(`/properties/${propertyId}/location-types/${typeId}`, { method: 'PATCH', body });
  }

  deleteLocationType(propertyId, typeId) {
    return this.request(`/properties/${propertyId}/location-types/${typeId}`, { method: 'DELETE' });
  }

  previewBulkRule(propertyId, body) {
    return this.request(`/properties/${propertyId}/locations/bulk-rule/preview`, { method: 'POST', body });
  }

  createBulkRule(propertyId, body) {
    return this.request(`/properties/${propertyId}/locations/bulk-rule`, { method: 'POST', body });
  }

  previewRoomList(propertyId, body) {
    return this.request(`/properties/${propertyId}/locations/room-list/preview`, { method: 'POST', body });
  }

  createRoomList(propertyId, body) {
    return this.request(`/properties/${propertyId}/locations/room-list`, { method: 'POST', body });
  }

  cloneProperty(propertyId, body) {
    return this.request(`/properties/${propertyId}/clone`, { method: 'POST', body });
  }

  async exportLocations(propertyId) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/properties/${propertyId}/locations/export`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Export failed (${res.status})`);
    return res.text();
  }

  // ---- AOS-REG-007 location attributes: floor plan file ----
  uploadFloorPlan(locationId, { filename, contentType, data }) {
    return this.request(`/locations/${locationId}/floor-plan`, { method: 'POST', body: { filename, contentType, data } });
  }

  removeFloorPlan(locationId) {
    return this.request(`/locations/${locationId}/floor-plan`, { method: 'DELETE' });
  }

  async floorPlanUrl(locationId) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/locations/${locationId}/floor-plan`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(res.status === 404 ? 'This location has no floor plan' : `Could not open the floor plan (${res.status})`);
    return URL.createObjectURL(await res.blob());
  }

  // ---- AOS-REG-004 / AOS-TAG-001 / AOS-TAG-006: location and Stock Point tags, NFC ----
  listSubjectTags({ propertyId, subjectType, subjectId }) {
    const q = new URLSearchParams({ propertyId, subjectType });
    if (subjectId) q.set('subjectId', subjectId);
    return this.request(`/tags/subjects?${q.toString()}`);
  }

  createSubjectTagBatch({ propertyId, subjectType, subjectIds, format = 'QR', labelSpec = 'A4-3x8' }) {
    return this.request('/tags/subject-batches', { method: 'POST', body: { propertyId, subjectType, subjectIds, format, labelSpec } });
  }

  async subjectTagLabelsPdf({ propertyId, subjectType, subjectIds, labelSpec = 'A4-3x8', skip = 0 }) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/tags/subject-labels`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ propertyId, subjectType, subjectIds, labelSpec, skip }),
    });
    if (!res.ok) {
      const payload = res.headers.get('content-type')?.includes('application/json') ? await res.json() : null;
      throw new Error(payload?.error?.message || `Could not print the labels (${res.status})`);
    }
    return URL.createObjectURL(await res.blob());
  }

  bindSubjectTag({ propertyId, subjectType, subjectId, code }) {
    return this.request('/tags/subject-bind', { method: 'POST', body: { propertyId, subjectType, subjectId, code } });
  }

  unbindSubjectTag(tagId, { reason, note }) {
    return this.request(`/tags/${tagId}/subject-unbind`, { method: 'POST', body: { reason, ...(note ? { note } : {}) } });
  }

  registerNfcTag({ propertyId, uid }) {
    return this.request('/tags/nfc', { method: 'POST', body: { propertyId, uid } });
  }

  // ---- AOS-STK-007 laundry cycle / AOS-TAG-006 RFID counts ----
  getLaundryDashboard({ propertyId, stockPointId }) {
    const q = new URLSearchParams({ propertyId });
    if (stockPointId) q.set('stockPointId', stockPointId);
    return this.request(`/stock-points/laundry?${q.toString()}`);
  }

  moveLaundry(body) {
    return this.request('/stock-points/laundry/moves', { method: 'POST', body });
  }

  listRfidMappings(propertyId) {
    return this.request(`/stock-points/rfid/mappings?propertyId=${propertyId}`);
  }

  addRfidMappings(body) {
    return this.request('/stock-points/rfid/mappings', { method: 'POST', body });
  }

  removeRfidMapping(id) {
    return this.request(`/stock-points/rfid/mappings/${id}`, { method: 'DELETE' });
  }

  previewRfidCount(body) {
    return this.request('/stock-points/rfid/count/preview', { method: 'POST', body });
  }

  submitRfidCount(body) {
    return this.request('/stock-points/rfid/count', { method: 'POST', body });
  }

  // ---- Master Item Catalogue (Admin only) ----
  listCatalogueItems({ search, categoryClass, source, propertyId, page = 1, pageSize = 100 } = {}) {
    const q = new URLSearchParams({ page, pageSize });
    if (search) q.set('search', search);
    if (categoryClass) q.set('categoryClass', categoryClass);
    if (source && source !== 'all') q.set('source', source);
    if (propertyId) q.set('propertyId', propertyId);
    return this.request(`/catalogue/items?${q.toString()}`);
  }

  createCatalogueItem(body) {
    return this.request('/catalogue/items', { method: 'POST', body });
  }

  updateCatalogueItem(id, body) {
    return this.request(`/catalogue/items/${id}`, { method: 'PATCH', body });
  }

  listCatalogueDuplicates() {
    return this.request('/catalogue/duplicates');
  }

  mergeCatalogueItems({ survivorId, duplicateId }) {
    return this.request('/catalogue/merge', { method: 'POST', body: { survivorId, duplicateId } });
  }

  listStarterPacks() {
    return this.request('/catalogue/starter-packs');
  }

  previewStarterPack(profile) {
    return this.request(`/catalogue/starter-packs/${encodeURIComponent(profile)}/preview`);
  }

  deployStarterPack({ profile, overrides }) {
    return this.request('/catalogue/starter-packs/deploy', { method: 'POST', body: { profile, overrides } });
  }

  async exportCatalogue({ search, categoryClass, source, propertyId } = {}) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const q = new URLSearchParams();
    if (search) q.set('search', search);
    if (categoryClass) q.set('categoryClass', categoryClass);
    if (source && source !== 'all') q.set('source', source);
    if (propertyId) q.set('propertyId', propertyId);
    const res = await fetch(`${API_BASE_URL}/catalogue/items/export?${q.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Export failed (${res.status})`);
    return res.text();
  }

  // ---- Catalogue: ownership, photos, categories, CSV import, suppliers ----
  getCatalogueLookups() {
    return this.request('/catalogue/lookups');
  }

  listCatalogueCategories() {
    return this.request('/catalogue/categories');
  }

  customiseCatalogueItem(id) {
    return this.request(`/catalogue/items/${id}/customise`, { method: 'POST' });
  }

  setCatalogueItemPhoto(id, { filename, contentType, data }) {
    return this.request(`/catalogue/items/${id}/photo`, { method: 'POST', body: { filename, contentType, data } });
  }

  removeCatalogueItemPhoto(id) {
    return this.request(`/catalogue/items/${id}/photo`, { method: 'DELETE' });
  }

  /** The photo as an object URL (the route needs the bearer token, so an <img src> cannot load it directly). */
  async fetchCatalogueItemPhoto(id) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/catalogue/items/${id}/photo`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`Photo unavailable (${res.status})`);
    return URL.createObjectURL(await res.blob());
  }

  async downloadCatalogueImportTemplate() {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/catalogue/import/template`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`Template download failed (${res.status})`);
    return res.text();
  }

  previewCatalogueImport(csv, { propertyId } = {}) {
    return this.request('/catalogue/import/preview', { method: 'POST', body: { csv, ...(propertyId ? { propertyId } : {}) } });
  }

  confirmCatalogueImport(csv, { skipInvalid = false, propertyId } = {}) {
    return this.request('/catalogue/import/confirm', { method: 'POST', body: { csv, skipInvalid, ...(propertyId ? { propertyId } : {}) } });
  }

  // ---- Catalogue: brand standard library (AOS-CAT-008) ----
  getBrandLibrary() {
    return this.request('/catalogue/brand-library');
  }

  setBrandLibrary(enabled) {
    return this.request('/catalogue/brand-library', { method: 'PUT', body: { enabled } });
  }

  setBrandStandard(itemIds, isBrandStandard) {
    return this.request('/catalogue/items/brand-standard', { method: 'POST', body: { itemIds, isBrandStandard } });
  }

  listSuppliers({ search, status = 'active' } = {}) {
    const q = new URLSearchParams({ status });
    if (search) q.set('search', search);
    return this.request(`/catalogue/suppliers?${q.toString()}`);
  }

  createSupplier(body) {
    return this.request('/catalogue/suppliers', { method: 'POST', body });
  }

  updateSupplier(id, body) {
    return this.request(`/catalogue/suppliers/${id}`, { method: 'PATCH', body });
  }

  // ---- Onboarding assist: invoice / photo drafts with deterministic fallback ----
  getAssistSettings() {
    return this.request('/onboarding-assist/settings');
  }

  setAssistEnabled(enabled) {
    return this.request('/onboarding-assist/settings', { method: 'PUT', body: { enabled } });
  }

  async downloadInvoiceCsvTemplate() {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/onboarding-assist/invoice-template.csv`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`Template download failed (${res.status})`);
    return res.text();
  }

  listInvoiceDrafts({ status = 'Draft', page = 1, pageSize = 20 } = {}) {
    return this.request(`/onboarding-assist/invoice-drafts?status=${status}&page=${page}&pageSize=${pageSize}`);
  }

  getInvoiceDraft(id) {
    return this.request(`/onboarding-assist/invoice-drafts/${id}`);
  }

  extractInvoice({ fileName, mimeType, data }) {
    return this.request('/onboarding-assist/invoice-drafts/extract', { method: 'POST', body: { fileName, mimeType, data } });
  }

  importInvoiceCsv(body) {
    return this.request('/onboarding-assist/invoice-drafts/import-csv', { method: 'POST', body });
  }

  createManualInvoiceDraft(body) {
    return this.request('/onboarding-assist/invoice-drafts', { method: 'POST', body });
  }

  updateInvoiceDraft(id, body) {
    return this.request(`/onboarding-assist/invoice-drafts/${id}`, { method: 'PUT', body });
  }

  confirmInvoiceDraft(id, body) {
    return this.request(`/onboarding-assist/invoice-drafts/${id}/confirm`, { method: 'POST', body });
  }

  discardInvoiceDraft(id) {
    return this.request(`/onboarding-assist/invoice-drafts/${id}/discard`, { method: 'POST' });
  }

  suggestFromPhoto({ fileName, mimeType, data, hint }) {
    return this.request('/onboarding-assist/photo-suggestions', { method: 'POST', body: { fileName, mimeType, data, ...(hint ? { hint } : {}) } });
  }

  // ---- Inwarding & Asset Tagging (Storekeeper / Admin) ----
  getInwardingLookups(propertyId) {
    return this.request(`/inwarding/lookups${propertyId ? `?propertyId=${propertyId}` : ''}`);
  }

  getInwardingSummary(propertyId) {
    return this.request(`/inwarding/summary?propertyId=${propertyId}`);
  }

  listInwardAssets({ propertyId, search, category, status, page = 1, pageSize = 50 }) {
    const q = new URLSearchParams({ propertyId, page, pageSize });
    if (search) q.set('search', search);
    if (category) q.set('category', category);
    if (status) q.set('status', status);
    return this.request(`/inwarding/assets?${q.toString()}`);
  }

  listPooledInventory({ propertyId, page = 1, pageSize = 50 }) {
    return this.request(`/inwarding/pooled?propertyId=${propertyId}&page=${page}&pageSize=${pageSize}`);
  }

  createInwardReceipt(body) {
    return this.request('/inwarding/receipts', { method: 'POST', body });
  }

  printAssetTags({ propertyId, assetIds }) {
    return this.request('/inwarding/tags/print', { method: 'POST', body: { propertyId, assetIds } });
  }

  // ---- Opening inventory (AOS-KIT-006) ----
  getOpeningInventory(propertyId) {
    return this.request(`/inwarding/opening${propertyId ? `?propertyId=${propertyId}` : ''}`);
  }

  getOpeningSpec(specId) {
    return this.request(`/inwarding/opening/specs/${specId}`);
  }

  previewOpeningSpec({ propertyId, locationTypeId, csv }) {
    return this.request('/inwarding/opening/specs/preview', { method: 'POST', body: { propertyId, locationTypeId, csv } });
  }

  importOpeningSpec({ propertyId, locationTypeId, csv, skipInvalid }) {
    return this.request('/inwarding/opening/specs', { method: 'POST', body: { propertyId, locationTypeId, csv, ...(skipInvalid ? { skipInvalid: true } : {}) } });
  }

  receiveOpeningItem(body) {
    return this.request('/inwarding/opening/receipts', { method: 'POST', body });
  }

  stampOpeningSpec(specId) {
    return this.request(`/inwarding/opening/specs/${specId}/stamp`, { method: 'POST' });
  }

  async downloadOpeningSpecTemplate() {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/inwarding/opening/template`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`Template download failed (${res.status})`);
    return res.text();
  }

  // ---- Room Kit Templates & Stamping (Property Manager / Admin) ----
  getRoomKitLookups(propertyId) {
    return this.request(`/room-kits/lookups${propertyId ? `?propertyId=${propertyId}` : ''}`);
  }

  getRoomKitSummary(propertyId) {
    return this.request(`/room-kits/summary?propertyId=${propertyId}`);
  }

  listRoomKitBlueprints(propertyId) {
    return this.request(`/room-kits/blueprints?propertyId=${propertyId}`);
  }

  getRoomKitBlueprint(id, propertyId) {
    return this.request(`/room-kits/blueprints/${id}${propertyId ? `?propertyId=${propertyId}` : ''}`);
  }

  createRoomKitBlueprint({ name, roomType, locationTypeId }) {
    return this.request('/room-kits/blueprints', { method: 'POST', body: { name, ...(roomType ? { roomType } : {}), ...(locationTypeId ? { locationTypeId } : {}) } });
  }

  updateRoomKitBlueprint(id, body) {
    return this.request(`/room-kits/blueprints/${id}`, { method: 'PATCH', body });
  }

  addRoomKitItem(blueprintId, body) {
    return this.request(`/room-kits/blueprints/${blueprintId}/items`, { method: 'POST', body });
  }

  updateRoomKitItem(blueprintId, itemId, quantity) {
    return this.request(`/room-kits/blueprints/${blueprintId}/items/${itemId}`, { method: 'PATCH', body: { quantity } });
  }

  removeRoomKitItem(blueprintId, itemId) {
    return this.request(`/room-kits/blueprints/${blueprintId}/items/${itemId}`, { method: 'DELETE' });
  }

  listRoomKitRooms({ propertyId, wingId, blueprintId, search, includeStamped = false, page = 1, pageSize = 200 }) {
    const q = new URLSearchParams({ propertyId, page, pageSize, includeStamped });
    if (wingId) q.set('wingId', wingId);
    if (blueprintId) q.set('blueprintId', blueprintId);
    if (search) q.set('search', search);
    return this.request(`/room-kits/rooms?${q.toString()}`);
  }

  stampRoomKits({ propertyId, blueprintId, locationIds }) {
    return this.request('/room-kits/stamp', { method: 'POST', body: { propertyId, blueprintId, locationIds } });
  }

  getRoomKitDiff(propertyId, blueprintId) {
    return this.request(`/room-kits/diff?propertyId=${propertyId}${blueprintId ? `&blueprintId=${blueprintId}` : ''}`);
  }

  listRoomKitDiffRooms({ propertyId, blueprintId, page = 1, pageSize = 25 }) {
    const q = new URLSearchParams({ propertyId, page, pageSize });
    if (blueprintId) q.set('blueprintId', blueprintId);
    return this.request(`/room-kits/diff/rooms?${q.toString()}`);
  }

  approveRoomKitDiff({ propertyId, blueprintId }) {
    return this.request('/room-kits/diff/approve', { method: 'POST', body: { propertyId, blueprintId } });
  }

  listRoomKitPendingRemovals({ propertyId, page = 1, pageSize = 25 }) {
    return this.request(`/room-kits/pending-removals?propertyId=${propertyId}&page=${page}&pageSize=${pageSize}`);
  }

  getRoomKitWizard(propertyId) {
    return this.request(`/room-kits/wizard?propertyId=${propertyId}`);
  }

  saveRoomKitWizard({ propertyId, currentStep, completedSteps }) {
    return this.request('/room-kits/wizard', { method: 'PUT', body: { propertyId, currentStep, completedSteps } });
  }

  // ---- Tag Generation & Binding Console (Storekeeper / Admin) ----
  getTagLookups(propertyId) {
    return this.request(`/tags/lookups${propertyId ? `?propertyId=${propertyId}` : ''}`);
  }

  listTags({ propertyId, search, state, page = 1, pageSize = 25 }) {
    const q = new URLSearchParams({ propertyId, page, pageSize });
    if (search) q.set('search', search);
    if (state) q.set('state', state);
    return this.request(`/tags?${q.toString()}`);
  }

  generateTagBatch({ propertyId, quantity, format, labelSpec }) {
    return this.request('/tags/batches', { method: 'POST', body: { propertyId, quantity, format, labelSpec } });
  }

  // Tag unbind / re-bind with reason (Storekeeper / Admin) and the scan resolver (every role that can see assets).
  resolveTagCode(code) {
    return this.request(`/tags/resolve?code=${encodeURIComponent(code)}`);
  }

  listBindableAssets({ propertyId, search }) {
    const q = new URLSearchParams({ propertyId });
    if (search) q.set('search', search);
    return this.request(`/tags/bindable-assets?${q.toString()}`);
  }

  unbindTag(tagId, { reason, note }) {
    return this.request(`/tags/${tagId}/unbind`, { method: 'POST', body: { reason, ...(note ? { note } : {}) } });
  }

  bindTagToAsset(tagId, { assetId, reason, note }) {
    return this.request(`/tags/${tagId}/bind`, { method: 'POST', body: { assetId, ...(reason ? { reason } : {}), ...(note ? { note } : {}) } });
  }

  // ---- Asset detail and timeline (every role that can see assets) ----
  getAssetDetail(assetId) {
    return this.request(`/assets/${assetId}`);
  }

  getAssetTimeline(assetId, { order = 'desc' } = {}) {
    return this.request(`/assets/${assetId}/timeline?order=${order}`);
  }

  setAssetCondition(assetId, { grade, reason, source, photo }) {
    return this.request(`/assets/${assetId}/condition`, { method: 'POST', body: { grade, ...(reason ? { reason } : {}), ...(source ? { source } : {}), ...(photo ? { photo } : {}) } });
  }

  async getAssetConditionPhoto(assetId, photoId) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/assets/${assetId}/condition-photos/${photoId}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`Photo could not be loaded (${res.status})`);
    return res.blob();
  }

  // ---- Tagged asset lifecycle, components, documents, register list and bulk edit (SRS 3.5) ----
  setAssetStatus(assetId, { status, reason }) {
    return this.request(`/assets/${assetId}/status`, { method: 'POST', body: { status, ...(reason ? { reason } : {}) } });
  }

  setAssetParent(assetId, { parentAssetId, note }) {
    return this.request(`/assets/${assetId}/parent`, { method: 'PUT', body: { parentAssetId: parentAssetId ?? null, ...(note ? { note } : {}) } });
  }

  listAssetDocuments(assetId) {
    return this.request(`/assets/${assetId}/documents`);
  }

  uploadAssetDocument(assetId, { kind, title, file }) {
    return this.request(`/assets/${assetId}/documents`, { method: 'POST', body: { kind, ...(title ? { title } : {}), file } });
  }

  deleteAssetDocument(assetId, docId) {
    return this.request(`/assets/${assetId}/documents/${docId}`, { method: 'DELETE' });
  }

  async downloadAssetDocument(assetId, docId) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/assets/${assetId}/documents/${docId}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`The document could not be downloaded (${res.status})`);
    return res.blob();
  }

  getAssetRegisterLookups(propertyId) {
    return this.request(`/assets/lookups${propertyId ? `?propertyId=${propertyId}` : ''}`);
  }

  listAssetRegister(filter = {}) {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(filter)) if (v !== undefined && v !== null && v !== '') q.set(k, v);
    return this.request(`/assets?${q.toString()}`);
  }

  previewAssetBulkEdit(body) {
    return this.request('/assets/bulk-edit/preview', { method: 'POST', body });
  }

  applyAssetBulkEdit(body) {
    return this.request('/assets/bulk-edit/apply', { method: 'POST', body });
  }

  // ---- First Scan Walk Binding (Field User / Admin) ----
  getScanWalkLookups(propertyId) {
    return this.request(`/scan-walk/lookups${propertyId ? `?propertyId=${propertyId}` : ''}`);
  }

  listScanWalkRooms({ propertyId, search, page = 1, pageSize = 50 }) {
    const q = new URLSearchParams({ propertyId, page, pageSize });
    if (search) q.set('search', search);
    return this.request(`/scan-walk/rooms?${q.toString()}`);
  }

  resolveScanWalkRoom({ propertyId, code }) {
    return this.request(`/scan-walk/rooms/resolve?${new URLSearchParams({ propertyId, code }).toString()}`);
  }

  getScanWalkRoomAssets(roomId, { page = 1, pageSize = 50 } = {}) {
    return this.request(`/scan-walk/rooms/${roomId}/assets?page=${page}&pageSize=${pageSize}`);
  }

  bindPhysicalTag({ assetId, tag, serialNumber }) {
    return this.request('/scan-walk/bindings', { method: 'POST', body: { assetId, tag, ...(serialNumber ? { serialNumber } : {}) } });
  }

  // First-walk exceptions (AOS-KIT-004): item absent / different model
  listWalkExceptions({ propertyId, locationId, status = 'open' }) {
    const q = new URLSearchParams({ propertyId, status });
    if (locationId) q.set('locationId', locationId);
    return this.request(`/scan-walk/exceptions?${q.toString()}`);
  }

  recordWalkException(body) {
    return this.request('/scan-walk/exceptions', { method: 'POST', body });
  }

  resolveWalkException(id, note) {
    return this.request(`/scan-walk/exceptions/${id}/resolve`, { method: 'POST', body: note ? { note } : {} });
  }

  // ---- Physical Audit Verification (Field User / Auditor / Admin) ----
  getAuditLookups(propertyId) {
    return this.request(`/audits/lookups${propertyId ? `?propertyId=${propertyId}` : ''}`);
  }

  listAuditRooms({ propertyId, search, page = 1, pageSize = 100 }) {
    const q = new URLSearchParams({ propertyId, page, pageSize });
    if (search) q.set('search', search);
    return this.request(`/audits/rooms?${q.toString()}`);
  }

  getAuditChecklist(batchId, roomId, { page = 1, pageSize = 100 } = {}) {
    return this.request(`/audits/batches/${batchId}/rooms/${roomId}/checklist?page=${page}&pageSize=${pageSize}`);
  }

  recordAuditScan(batchId, { roomId, tag, condition, notes, photoUrl, clientScanId, scannedAt, deviceId, method }) {
    return this.request(`/audits/batches/${batchId}/scans`, {
      method: 'POST',
      body: { roomId, tag, ...(condition ? { condition } : {}), ...(notes ? { notes } : {}), ...(photoUrl ? { photoUrl } : {}), ...(clientScanId ? { clientScanId } : {}), ...(scannedAt ? { scannedAt } : {}), ...(deviceId ? { deviceId } : {}), ...(method ? { method } : {}) },
    });
  }

  /** Opens a room by its scanned location tag or printed location code. */
  resolveAuditLocation(propertyId, code) {
    return this.request(`/audits/locations/resolve?propertyId=${propertyId}&code=${encodeURIComponent(code)}`);
  }

  /** "Move here": relocates a mislocated asset to the room it was just found in. */
  moveAuditAssetHere(batchId, assetId, { roomId, deviceId }) {
    return this.request(`/audits/batches/${batchId}/assets/${assetId}/move-here`, { method: 'POST', body: { roomId, ...(deviceId ? { deviceId } : {}) } });
  }

  /** Unified exception queue (Missing, Damaged, Extra, Mislocated, Over-par, Under-par). */
  listAuditExceptions({ propertyId, type, status = 'open', page = 1, pageSize = 50 } = {}) {
    const q = new URLSearchParams({ status, page: String(page), pageSize: String(pageSize) });
    if (propertyId) q.set('propertyId', propertyId);
    if (type) q.set('type', type);
    return this.request(`/audits/exceptions?${q.toString()}`);
  }

  resolveAuditException({ source, sourceId, type, resolution, notes }) {
    return this.request('/audits/exceptions/resolve', { method: 'POST', body: { source, sourceId, type, resolution, ...(notes ? { notes } : {}) } });
  }

  // ---- Pooled stock movement ledger (issue / return / transfer between Stock Points) ----
  getStockMoveLookups(propertyId) {
    return this.request(`/stock-movements/lookups${propertyId ? `?propertyId=${propertyId}` : ''}`);
  }

  listStockMovements({ propertyId, pooledInventoryId, type, page = 1, pageSize = 25 }) {
    const q = new URLSearchParams({ propertyId: String(propertyId), page: String(page), pageSize: String(pageSize) });
    if (pooledInventoryId) q.set('pooledInventoryId', pooledInventoryId);
    if (type) q.set('type', type);
    return this.request(`/stock-movements?${q.toString()}`);
  }

  listOpenStockIssues(propertyId) {
    return this.request(`/stock-movements/open-issues?propertyId=${propertyId}`);
  }

  issueStock(body) {
    return this.request('/stock-movements/issue', { method: 'POST', body });
  }

  returnStock(body) {
    return this.request('/stock-movements/return', { method: 'POST', body });
  }

  transferStock(body) {
    return this.request('/stock-movements/transfer', { method: 'POST', body });
  }

  // ---- Count variance threshold per tenant ----
  getCountPolicy(propertyId) {
    return this.request(`/blind-counts/policy?propertyId=${propertyId}`);
  }

  setCountPolicy(propertyId, thresholdPct) {
    return this.request('/blind-counts/policy', { method: 'PUT', body: { propertyId, thresholdPct } });
  }

  // ---- Custody returns and inter-property transfers ----
  listCustodyOpenIssues(propertyId) {
    return this.request(`/custody/returns?propertyId=${propertyId}`);
  }

  returnCustodyAsset(body) {
    return this.request('/custody/returns', { method: 'POST', body });
  }

  listIncomingCustody(propertyId) {
    return this.request(`/custody/incoming?propertyId=${propertyId}`);
  }

  receiveCustodyTransfer(id, body) {
    return this.request(`/custody/transfers/${id}/receive`, { method: 'POST', body });
  }

  /** Damage photo for an audit walk (base64 JPEG / PNG / WebP); returns { id, url } - the url is sent with the scan. */
  uploadAuditPhoto(batchId, { filename, contentType, data }) {
    return this.request(`/audits/batches/${batchId}/photos`, { method: 'POST', body: { filename, contentType, data } });
  }

  /** Loads an audit damage photo with the signed-in token. Accepts the absolute link stored on the scan. */
  async getAuditPhotoBlob(photoUrl) {
    const path = new URL(photoUrl, window.location.origin).pathname.replace(/^\/api/, '');
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`Photo failed to load (${res.status})`);
    return res.blob();
  }

  signOffAuditRoom(batchId, roomId) {
    return this.request(`/audits/batches/${batchId}/rooms/${roomId}/signoff`, { method: 'POST' });
  }

  syncAuditOperations(batchId, operations) {
    return this.request(`/audits/batches/${batchId}/sync`, { method: 'POST', body: { operations } });
  }

  // ---- Audit Reconciliation Console (Property Manager / Finance Controller / Auditor read-only / Admin) ----
  getReconciliationLookups() {
    return this.request('/reconciliation/lookups');
  }

  getReconciliationSummary(batchId) {
    return this.request(`/reconciliation/batches/${batchId}/summary`);
  }

  listReconciliationDiscrepancies(batchId, { type = 'all', search, page = 1, pageSize = 50 } = {}) {
    const q = new URLSearchParams({ type, page, pageSize });
    if (search) q.set('search', search);
    return this.request(`/reconciliation/batches/${batchId}/discrepancies?${q.toString()}`);
  }

  getReconciliationPooled(batchId) {
    return this.request(`/reconciliation/batches/${batchId}/pooled`);
  }

  decideReconciliationPooled(batchId, pooledId, countedQuantity) {
    return this.request(`/reconciliation/batches/${batchId}/pooled/${pooledId}/decision`, { method: 'POST', body: { countedQuantity } });
  }

  approveAuditRelocation(batchId, assetId) {
    return this.request(`/reconciliation/batches/${batchId}/assets/${assetId}/relocation/approve`, { method: 'POST' });
  }

  returnAuditAsset(batchId, assetId) {
    return this.request(`/reconciliation/batches/${batchId}/assets/${assetId}/relocation/return`, { method: 'POST' });
  }

  approveAllAuditRelocations(batchId) {
    return this.request(`/reconciliation/batches/${batchId}/relocations/approve-all`, { method: 'POST' });
  }

  investigateMissingAsset(batchId, assetId, note) {
    return this.request(`/reconciliation/batches/${batchId}/assets/${assetId}/investigation`, { method: 'POST', body: { note } });
  }

  submitAuditWriteOff(batchId, assetId, reason) {
    return this.request(`/reconciliation/batches/${batchId}/assets/${assetId}/write-off`, { method: 'POST', body: reason ? { reason } : {} });
  }

  bulkWriteOffLowValue(batchId) {
    return this.request(`/reconciliation/batches/${batchId}/write-offs/bulk-low-value`, { method: 'POST' });
  }

  decideAuditWriteOff(writeOffId, { decision, notes }) {
    return this.request(`/reconciliation/write-offs/${writeOffId}/decision`, { method: 'POST', body: { decision, ...(notes ? { notes } : {}) } });
  }

  reconcileAuditBatch(batchId, { remarks }) {
    return this.request(`/reconciliation/batches/${batchId}/reconcile`, { method: 'POST', body: { certified: true, ...(remarks ? { remarks } : {}) } });
  }

  getAuditVarianceReport(batchId) {
    return this.request(`/reconciliation/batches/${batchId}/report`);
  }

  async exportReconciliation(batchId) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/reconciliation/batches/${batchId}/export`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`Export failed (${res.status})`);
    return res.text();
  }

  // ---- Blind Counts & Variance Console (Field User counts; managers investigate) ----
  getBlindCountLookups(propertyId) {
    return this.request(`/blind-counts/lookups${propertyId ? `?propertyId=${propertyId}` : ''}`);
  }

  getBlindCountSheet({ propertyId, stockPointId }) {
    return this.request(`/blind-counts/sheet?propertyId=${propertyId}&stockPointId=${stockPointId}`);
  }

  listBlindCountLines({ propertyId, status, page = 1, pageSize = 25 }) {
    const q = new URLSearchParams({ propertyId, page, pageSize });
    if (status) q.set('status', status);
    return this.request(`/blind-counts/sessions?${q.toString()}`);
  }

  submitBlindCount({ propertyId, stockPointId, counts }) {
    return this.request('/blind-counts/sessions', { method: 'POST', body: { propertyId, stockPointId, counts } });
  }

  logBreakage({ propertyId, pooledInventoryId, quantity, reasonCode, photos = [] }) {
    return this.request('/blind-counts/breakage', { method: 'POST', body: { propertyId, pooledInventoryId, quantity, reasonCode, photos } });
  }

  // ---- Banquet Event Kits & Batch Reconciliation ----
  getEventKitLookups(propertyId) {
    return this.request(`/event-kits/lookups${propertyId ? `?propertyId=${propertyId}` : ''}`);
  }

  getEventKitSummary(propertyId) {
    return this.request(`/event-kits/summary?propertyId=${propertyId}`);
  }

  listEventKits({ propertyId, search, status, page = 1, pageSize = 25 }) {
    const q = new URLSearchParams({ propertyId: String(propertyId), page: String(page), pageSize: String(pageSize) });
    if (search) q.set('search', search);
    if (status) q.set('status', status);
    return this.request(`/event-kits?${q.toString()}`);
  }

  getEventKit(id, propertyId) {
    return this.request(`/event-kits/${id}?propertyId=${propertyId}`);
  }

  createEventKit(body) {
    return this.request('/event-kits', { method: 'POST', body });
  }

  checkoutEventKit(id, body) {
    return this.request(`/event-kits/${id}/checkout`, { method: 'POST', body });
  }

  reconcileEventKit(id, body) {
    return this.request(`/event-kits/${id}/reconcile`, { method: 'POST', body });
  }

  // ---- Account plans, Xero billing and suspension ladder ----
  listAccountPlans({ search, status, page = 1, pageSize = 100 } = {}) {
    const q = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (search) q.set('search', search);
    if (status) q.set('status', status);
    return this.request(`/account-plans?${q.toString()}`);
  }

  getAccountPlan(propertyId) {
    return this.request(`/account-plans/properties/${propertyId}`);
  }

  getAccountStanding() {
    return this.request('/account-plans/standing');
  }

  setAccountPlanTier(propertyId, planTier) {
    return this.request(`/account-plans/properties/${propertyId}/plan`, { method: 'PUT', body: { planTier } });
  }

  simulateAccountOverdue(propertyId, days) {
    return this.request(`/account-plans/properties/${propertyId}/simulate-overdue`, { method: 'POST', body: { days } });
  }

  syncAccountInvoices(propertyId) {
    return this.request('/account-plans/sync-invoices', { method: 'POST', body: propertyId ? { propertyId } : {} });
  }

  // ---- Integration fabric connectors (PunchlistOS / generic CMMS), review queue and CSV ingestion ----
  getIntegrationOverview(propertyId) {
    return this.request(`/integrations/overview?propertyId=${propertyId}`);
  }

  listIntegrationProperties({ search, page = 1, pageSize = 25 } = {}) {
    const q = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (search) q.set('search', search);
    return this.request(`/integrations/properties?${q.toString()}`);
  }

  pairIntegrationConnector(body) {
    return this.request('/integrations/connectors', { method: 'POST', body });
  }

  setIntegrationConnectorActive(id, active) {
    return this.request(`/integrations/connectors/${id}/${active ? 'activate' : 'deactivate'}`, { method: 'POST' });
  }

  testIntegrationHandshake(id) {
    return this.request(`/integrations/connectors/${id}/handshake`, { method: 'POST' });
  }

  retryIntegrationOutbound(propertyId) {
    return this.request('/integrations/outbound/retry', { method: 'POST', body: { propertyId } });
  }

  runIntegrationReconciliation(propertyId) {
    return this.request('/integrations/reconciliation/run', { method: 'POST', body: { propertyId } });
  }

  verifyXeroConnection() {
    return this.request('/integrations/xero/verify', { method: 'POST' });
  }

  listIntegrationReviewQueue({ propertyId, status = 'pending', page = 1, pageSize = 10 }) {
    return this.request(`/integrations/review-queue?${new URLSearchParams({ propertyId: String(propertyId), status, page: String(page), pageSize: String(pageSize) }).toString()}`);
  }

  bindIntegrationReviewItem(id, body) {
    return this.request(`/integrations/review-queue/${id}/bind`, { method: 'POST', body });
  }

  dismissIntegrationReviewItem(id) {
    return this.request(`/integrations/review-queue/${id}/dismiss`, { method: 'POST' });
  }

  saveIntegrationCsvMapping(propertyId, mapping) {
    return this.request('/integrations/csv-mapping', { method: 'PUT', body: { propertyId, mapping } });
  }

  importMaintenanceCsv(body) {
    return this.request('/integrations/imports/maintenance-csv', { method: 'POST', body });
  }

  importPmsRoomList(body) {
    return this.request('/integrations/imports/pms-rooms', { method: 'POST', body });
  }

  async downloadRawAssetCsv(propertyId) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/account-plans/properties/${propertyId}/raw-export`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) {
      let message = `Export failed (${res.status})`;
      try {
        message = (await res.json())?.error?.message || message;
      } catch {
        /* non-JSON error body */
      }
      throw new Error(message);
    }
    const disposition = res.headers.get('Content-Disposition') || '';
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] || 'raw-assets.csv';
    return { blob: await res.blob(), filename };
  }

  // ---- Guest Loaners & Overdue Tracker ----
  getGuestLoanerLookups(propertyId) {
    return this.request(`/guest-loaners/lookups${propertyId ? `?propertyId=${propertyId}` : ''}`);
  }

  getGuestLoanerSummary(propertyId) {
    return this.request(`/guest-loaners/summary?propertyId=${propertyId}`);
  }

  listGuestLoans({ propertyId, search, status, page = 1, pageSize = 25 }) {
    const q = new URLSearchParams({ propertyId: String(propertyId), page: String(page), pageSize: String(pageSize) });
    if (search) q.set('search', search);
    if (status) q.set('status', status);
    return this.request(`/guest-loaners?${q.toString()}`);
  }

  issueGuestLoan(body) {
    return this.request('/guest-loaners', { method: 'POST', body });
  }

  returnGuestLoan(id, body) {
    return this.request(`/guest-loaners/${id}/return`, { method: 'POST', body });
  }

  nudgeGuestLoan(id, body) {
    return this.request(`/guest-loaners/${id}/nudge`, { method: 'POST', body });
  }

  investigateAuditException(id) {
    return this.request(`/blind-counts/exceptions/${id}/investigate`, { method: 'POST' });
  }

  async exportBlindCounts(propertyId) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/blind-counts/export?propertyId=${propertyId}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`Export failed (${res.status})`);
    return res.text();
  }

  // ---- Category Book Policies (Finance Controller; versioned, effective-dated) ----
  getBookPolicyLookups() {
    return this.request('/book-policies/lookups');
  }

  listBookPolicyCategories({ search, page = 1, pageSize = 25 } = {}) {
    const q = new URLSearchParams({ page, pageSize });
    if (search) q.set('search', search);
    return this.request(`/book-policies/categories?${q.toString()}`);
  }

  getBookPolicyCategory(id, period) {
    return this.request(`/book-policies/categories/${id}${period ? `?period=${period}` : ''}`);
  }

  saveBookPolicyVersion(body) {
    return this.request('/book-policies/versions', { method: 'POST', body });
  }

  closeAccountingPeriod(period) {
    return this.request(`/book-policies/periods/${period}/close`, { method: 'POST' });
  }

  // ---- Opening fixed asset balances (Finance Controller) ----
  getOpeningBalanceOverview() {
    return this.request('/opening-balances/overview');
  }

  async downloadOpeningBalanceTemplate() {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/opening-balances/template`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`Template download failed (${res.status})`);
    return res.text();
  }

  uploadOpeningBalances({ fileName, content }) {
    return this.request('/opening-balances/imports', { method: 'POST', body: { fileName, content } });
  }

  listOpeningBalanceLines(importId, { page = 1, pageSize = 10 } = {}) {
    return this.request(`/opening-balances/imports/${importId}/lines?page=${page}&pageSize=${pageSize}`);
  }

  confirmOpeningBalances(importId, { controlCost, controlAccumDep }) {
    return this.request(`/opening-balances/imports/${importId}/confirm`, { method: 'POST', body: { controlCost, controlAccumDep } });
  }

  // ---- Monthly IFRS depreciation runs and GL journals (Finance Controller) ----
  getDepreciationOverview({ propertyId, period } = {}) {
    const q = new URLSearchParams();
    if (propertyId) q.set('propertyId', propertyId);
    if (period) q.set('period', period);
    return this.request(`/depreciation/overview${q.toString() ? `?${q.toString()}` : ''}`);
  }

  getDepreciationSchedule({ propertyId, period, view = 'capital', search, category, department, page = 1, pageSize = 25 }) {
    const q = new URLSearchParams({ propertyId, period, view, page, pageSize });
    if (search) q.set('search', search);
    if (category) q.set('category', category);
    if (department) q.set('department', department);
    return this.request(`/depreciation/schedule?${q.toString()}`);
  }

  createDepreciationRun({ propertyId, period }) {
    return this.request('/depreciation/runs', { method: 'POST', body: { propertyId, period } });
  }

  getDepreciationRun(id) {
    return this.request(`/depreciation/runs/${id}`);
  }

  postDepreciationRun(id) {
    return this.request(`/depreciation/runs/${id}/post`, { method: 'POST' });
  }

  recalculateDepreciationRun(id) {
    return this.request(`/depreciation/runs/${id}/recalculate`, { method: 'POST' });
  }

  discardDepreciationRun(id) {
    return this.request(`/depreciation/runs/${id}`, { method: 'DELETE' });
  }

  postDepreciationAdjustment(body) {
    return this.request('/depreciation/adjustments', { method: 'POST', body });
  }

  postDepreciationToXero(id) {
    return this.request(`/depreciation/runs/${id}/xero`, { method: 'POST' });
  }

  async downloadDepreciationExport(id, target, format = 'csv') {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/depreciation/runs/${id}/export?target=${target}&format=${format}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) {
      let message = `Export failed (${res.status})`;
      try {
        message = (await res.json())?.error?.message || message;
      } catch {
        /* non-JSON error body */
      }
      throw new Error(message);
    }
    const disposition = res.headers.get('Content-Disposition') || '';
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] || `gl-batch.${format}`;
    return { blob: await res.blob(), filename };
  }

  // ---- Asset disposals, write-offs and derecognition (IAS 16) ----
  getDisposalOverview({ propertyId, period } = {}) {
    const q = new URLSearchParams();
    if (propertyId) q.set('propertyId', propertyId);
    if (period) q.set('period', period);
    return this.request(`/disposals/overview${q.toString() ? `?${q.toString()}` : ''}`);
  }

  listDisposals({ propertyId, search, type, status, period, page = 1, pageSize = 10 }) {
    const q = new URLSearchParams({ propertyId, page, pageSize });
    if (search) q.set('search', search);
    if (type) q.set('type', type);
    if (status) q.set('status', status);
    if (period) q.set('period', period);
    return this.request(`/disposals?${q.toString()}`);
  }

  getDisposableAssets({ propertyId, search, limit = 15 }) {
    const q = new URLSearchParams({ propertyId, limit });
    if (search) q.set('search', search);
    return this.request(`/disposals/assets?${q.toString()}`);
  }

  previewDisposal(body) {
    return this.request('/disposals/preview', { method: 'POST', body });
  }

  createDisposal(body) {
    return this.request('/disposals', { method: 'POST', body });
  }

  getDisposal(id) {
    return this.request(`/disposals/${id}`);
  }

  endorseDisposal(id, note) {
    return this.request(`/disposals/${id}/endorse`, { method: 'POST', body: note ? { note } : {} });
  }

  approveDisposal(id) {
    return this.request(`/disposals/${id}/approve`, { method: 'POST' });
  }

  rejectDisposal(id, reason) {
    return this.request(`/disposals/${id}/reject`, { method: 'POST', body: { reason } });
  }

  async downloadDisposalSchedule({ propertyId, search, type, status, period }) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const q = new URLSearchParams({ propertyId });
    if (search) q.set('search', search);
    if (type) q.set('type', type);
    if (status) q.set('status', status);
    if (period) q.set('period', period);
    const res = await fetch(`${API_BASE_URL}/disposals/export?${q.toString()}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) {
      let message = `Export failed (${res.status})`;
      try {
        message = (await res.json())?.error?.message || message;
      } catch {
        /* non-JSON error body */
      }
      throw new Error(message);
    }
    const disposition = res.headers.get('Content-Disposition') || '';
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] || 'write-off-schedule.csv';
    return { blob: await res.blob(), filename };
  }

  // ---- Fixed Asset Register (FAR) report as at a closed period ----
  getFarOverview({ propertyId } = {}) {
    return this.request(`/fixed-asset-register/overview${propertyId ? `?propertyId=${propertyId}` : ''}`);
  }

  getFarReport({ propertyId, period, groupBy = 'category', page = 1, pageSize = 50 }) {
    const q = new URLSearchParams({ period, groupBy, page, pageSize });
    if (propertyId) q.set('propertyId', propertyId);
    return this.request(`/fixed-asset-register/report?${q.toString()}`);
  }

  getFarAssets({ propertyId, period, groupBy = 'category', group, search, page = 1, pageSize = 25 }) {
    const q = new URLSearchParams({ period, groupBy, page, pageSize });
    if (propertyId) q.set('propertyId', propertyId);
    if (group !== undefined && group !== null) q.set('group', group);
    if (search) q.set('search', search);
    return this.request(`/fixed-asset-register/assets?${q.toString()}`);
  }

  async downloadFarExport({ propertyId, period, groupBy = 'category', format = 'csv' }) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const q = new URLSearchParams({ period, groupBy, format });
    if (propertyId) q.set('propertyId', propertyId);
    const res = await fetch(`${API_BASE_URL}/fixed-asset-register/export?${q.toString()}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) {
      let message = `Export failed (${res.status})`;
      try {
        message = (await res.json())?.error?.message || message;
      } catch {
        /* non-JSON error body */
      }
      throw new Error(message);
    }
    const disposition = res.headers.get('Content-Disposition') || '';
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] || `fixed-asset-register.${format}`;
    return { blob: await res.blob(), filename };
  }

  // ---- Location contents sheet (AOS-RPT-004) ----
  getLocationContentsLookups(propertyId) {
    return this.request(`/location-contents/lookups${propertyId ? `?propertyId=${propertyId}` : ''}`);
  }

  getLocationContents(locationId) {
    return this.request(`/location-contents/${locationId}`);
  }

  async downloadLocationContents(locationId, format = 'csv') {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/location-contents/${locationId}/export?format=${format}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) {
      let message = `Export failed (${res.status})`;
      try {
        message = (await res.json())?.error?.message || message;
      } catch {
        /* non-JSON error body */
      }
      throw new Error(message);
    }
    const disposition = res.headers.get('Content-Disposition') || '';
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] || `location-contents.${format}`;
    return { blob: await res.blob(), filename };
  }

  // ---- Executive asset valuation dashboard (any signed-in role) ----
  getAnalyticsDashboard({ propertyId, department, category } = {}) {
    const q = new URLSearchParams();
    if (propertyId) q.set('propertyId', propertyId);
    if (department) q.set('department', department);
    if (category) q.set('category', category);
    const qs = q.toString();
    return this.request(`/analytics/dashboard${qs ? `?${qs}` : ''}`);
  }

  async downloadAnalyticsExport({ propertyId, department, category, format = 'xlsx' }) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const q = new URLSearchParams({ format });
    if (propertyId) q.set('propertyId', propertyId);
    if (department) q.set('department', department);
    if (category) q.set('category', category);
    const res = await fetch(`${API_BASE_URL}/analytics/dashboard/export?${q.toString()}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) {
      let message = `Export failed (${res.status})`;
      try {
        message = (await res.json())?.error?.message || message;
      } catch {
        /* non-JSON error body */
      }
      throw new Error(message);
    }
    const disposition = res.headers.get('Content-Disposition') || '';
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] || `asset-valuation-dashboard.${format}`;
    return { blob: await res.blob(), filename };
  }

  // ---- Audit Schedules (Property Manager manages; Department Head / Auditor read-only) ----
  getAuditScheduleLookups(propertyId) {
    return this.request(`/audit-schedules/lookups${propertyId ? `?propertyId=${propertyId}` : ''}`);
  }

  listAuditSchedules(propertyId, { page = 1, pageSize = 50 } = {}) {
    return this.request(`/audit-schedules?propertyId=${propertyId}&page=${page}&pageSize=${pageSize}`);
  }

  createAuditSchedule(body) {
    return this.request('/audit-schedules', { method: 'POST', body });
  }

  setAuditScheduleStatus(scheduleId, status) {
    return this.request(`/audit-schedules/${scheduleId}/status`, { method: 'PATCH', body: { status } });
  }

  getAuditScheduleDashboard(propertyId, { asOf, page = 1, pageSize = 50 } = {}) {
    return this.request(`/audit-schedules/dashboard?propertyId=${propertyId}&page=${page}&pageSize=${pageSize}${asOf ? `&asOf=${asOf}` : ''}`);
  }

  listOverdueAudits(propertyId, { asOf, page = 1, pageSize = 50 } = {}) {
    return this.request(`/audit-schedules/overdue?propertyId=${propertyId}&page=${page}&pageSize=${pageSize}${asOf ? `&asOf=${asOf}` : ''}`);
  }

  listAuditCycleTasks(cycleId, { status, asOf, page = 1, pageSize = 50 } = {}) {
    return this.request(`/audit-schedules/cycles/${cycleId}/tasks?page=${page}&pageSize=${pageSize}${status ? `&status=${status}` : ''}${asOf ? `&asOf=${asOf}` : ''}`);
  }

  getMyAuditTasks({ page = 1, pageSize = 50 } = {}) {
    return this.request(`/audit-schedules/my-tasks?page=${page}&pageSize=${pageSize}`);
  }

  runAuditScheduleBatch({ propertyId, asOf } = {}) {
    return this.request('/audit-schedules/run', { method: 'POST', body: { ...(propertyId ? { propertyId } : {}), ...(asOf ? { asOf } : {}) } });
  }

  // ---- Stock Points & Par Management (Storekeeper / Admin) ----
  getStockPointLookups(propertyId) {
    return this.request(`/stock-points/lookups${propertyId ? `?propertyId=${propertyId}` : ''}`);
  }

  getStockPointSummary(propertyId) {
    return this.request(`/stock-points/summary?propertyId=${propertyId}`);
  }

  listStockPointLines({ propertyId, search, category, status, page = 1, pageSize = 25 }) {
    const q = new URLSearchParams({ propertyId, page, pageSize });
    if (search) q.set('search', search);
    if (category) q.set('category', category);
    if (status) q.set('status', status);
    return this.request(`/stock-points?${q.toString()}`);
  }

  createStockPoint(body) {
    return this.request('/stock-points', { method: 'POST', body });
  }

  applyParLevel(body) {
    return this.request('/stock-points/par-levels', { method: 'POST', body });
  }

  // ---- Asset Custody & Movements ----
  getCustodyLookups(propertyId) {
    return this.request(`/custody/lookups${propertyId ? `?propertyId=${propertyId}` : ''}`);
  }

  getCustodySummary(propertyId) {
    return this.request(`/custody/summary?propertyId=${propertyId}`);
  }

  listCustodyTransfers({ propertyId, search, status, department, page = 1, pageSize = 25 }) {
    const q = new URLSearchParams({ propertyId, page, pageSize });
    if (search) q.set('search', search);
    if (status) q.set('status', status);
    if (department) q.set('department', department);
    return this.request(`/custody/transfers?${q.toString()}`);
  }

  listCustodyRegister({ propertyId, search, department, page = 1, pageSize = 25 }) {
    const q = new URLSearchParams({ propertyId, page, pageSize });
    if (search) q.set('search', search);
    if (department) q.set('department', department);
    return this.request(`/custody/register?${q.toString()}`);
  }

  getCustodyTransfer(id, propertyId) {
    return this.request(`/custody/transfers/${id}?propertyId=${propertyId}`);
  }

  initiateCustodyTransfer(body) {
    return this.request('/custody/transfers', { method: 'POST', body });
  }

  listOverdueCustody({ propertyId, department, page = 1, pageSize = 25 }) {
    const q = new URLSearchParams({ propertyId, page, pageSize });
    if (department) q.set('department', department);
    return this.request(`/custody/overdue?${q.toString()}`);
  }

  runCustodyNudges(propertyId) {
    return this.request('/custody/overdue/run-nudges', { method: 'POST', body: { propertyId } });
  }

  escalateCustodyTransfer(id, propertyId) {
    return this.request(`/custody/transfers/${id}/escalate`, { method: 'POST', body: { propertyId } });
  }

  approveCustodyTransfer(id, propertyId) {
    return this.request(`/custody/transfers/${id}/approve`, { method: 'POST', body: { propertyId } });
  }

  acceptCustodyTransfer(id, body) {
    return this.request(`/custody/transfers/${id}/accept`, { method: 'POST', body });
  }

  // ---- Maintenance incident logging, native Repair Records and Linked Maintenance System ----
  getMaintenanceOverview({ propertyId } = {}) {
    return this.request(`/maintenance/overview${propertyId ? `?propertyId=${encodeURIComponent(propertyId)}` : ''}`);
  }

  listMaintenanceIncidents({ propertyId, search, severity, status, locationId, page = 1, pageSize = 10 }) {
    const q = new URLSearchParams({ propertyId, page, pageSize });
    if (search) q.set('search', search);
    if (severity) q.set('severity', severity);
    if (status) q.set('status', status);
    if (locationId) q.set('locationId', locationId);
    return this.request(`/maintenance/incidents?${q.toString()}`);
  }

  searchMaintenanceAssets({ propertyId, q, limit = 10 }) {
    const params = new URLSearchParams({ propertyId, q, limit });
    return this.request(`/maintenance/assets?${params.toString()}`);
  }

  createMaintenanceIncident(body) {
    return this.request('/maintenance/incidents', { method: 'POST', body });
  }

  getMaintenanceIncident(id) {
    return this.request(`/maintenance/incidents/${id}`);
  }

  resolveMaintenanceIncident(id) {
    return this.request(`/maintenance/incidents/${id}/resolve`, { method: 'POST' });
  }

  recordRepairCost(id, cost) {
    return this.request(`/maintenance/incidents/${id}/cost`, { method: 'POST', body: { cost } });
  }

  // ---- Repair Records workbench: completion, cost classification and the Repair-or-Replace signal ----
  listRepairRecords({ propertyId, search, status, replacementOnly, page = 1, pageSize = 10 }) {
    const q = new URLSearchParams({ propertyId, page, pageSize });
    if (search) q.set('search', search);
    if (status) q.set('status', status);
    if (replacementOnly) q.set('replacementOnly', 'true');
    return this.request(`/maintenance/repairs?${q.toString()}`);
  }

  getRepairRecord(id) {
    return this.request(`/maintenance/repairs/${id}`);
  }

  saveRepairProgress(id, body) {
    return this.request(`/maintenance/repairs/${id}/progress`, { method: 'PUT', body });
  }

  completeRepairRecord(id, body) {
    return this.request(`/maintenance/repairs/${id}/complete`, { method: 'POST', body });
  }

  // ---- Asset Replacements: retire the old asset and register its successor ----
  listReplacements({ propertyId, status, search, page = 1, pageSize = 10 }) {
    const q = new URLSearchParams({ propertyId, page, pageSize });
    if (status) q.set('status', status);
    if (search) q.set('search', search);
    return this.request(`/maintenance/replacements?${q.toString()}`);
  }

  getReplacementContext({ assetId, disposalType = 'scrap', proceeds = 0 }) {
    const q = new URLSearchParams({ assetId, disposalType, proceeds });
    return this.request(`/maintenance/replacements/context?${q.toString()}`);
  }

  searchReplacementCatalogue({ q, limit = 15 } = {}) {
    const params = new URLSearchParams({ limit });
    if (q) params.set('q', q);
    return this.request(`/maintenance/replacements/catalogue?${params.toString()}`);
  }

  executeReplacement(body) {
    return this.request('/maintenance/replacements', { method: 'POST', body });
  }

  executeReplacementProposal(id, body) {
    return this.request(`/maintenance/replacements/${id}/execute`, { method: 'POST', body });
  }

  dismissReplacement(id) {
    return this.request(`/maintenance/replacements/${id}/dismiss`, { method: 'POST' });
  }

  // ---- Preventive maintenance schedules, PM tasks and warranty-expiry alerts ----
  getPmSummary({ propertyId, asOf }) {
    const q = new URLSearchParams({ propertyId });
    if (asOf) q.set('asOf', asOf);
    return this.request(`/maintenance/pm/summary?${q.toString()}`);
  }

  searchPmTargets({ propertyId, q, limit = 10 }) {
    return this.request(`/maintenance/pm/targets?${new URLSearchParams({ propertyId, q, limit }).toString()}`);
  }

  listPmSchedules({ propertyId, search, page = 1, pageSize = 10 }) {
    const q = new URLSearchParams({ propertyId, page, pageSize });
    if (search) q.set('search', search);
    return this.request(`/maintenance/pm/schedules?${q.toString()}`);
  }

  createPmSchedule(body) {
    return this.request('/maintenance/pm/schedules', { method: 'POST', body });
  }

  deactivatePmSchedule(id) {
    return this.request(`/maintenance/pm/schedules/${id}/deactivate`, { method: 'POST' });
  }

  listPmTasks({ propertyId, status, overdueOnly, search, asOf, page = 1, pageSize = 10 }) {
    const q = new URLSearchParams({ propertyId, page, pageSize });
    if (status) q.set('status', status);
    if (overdueOnly) q.set('overdueOnly', 'true');
    if (search) q.set('search', search);
    if (asOf) q.set('asOf', asOf);
    return this.request(`/maintenance/pm/tasks?${q.toString()}`);
  }

  completePmTask(id, body) {
    return this.request(`/maintenance/pm/tasks/${id}/complete`, { method: 'POST', body });
  }

  listWarrantyAlerts({ propertyId, asOf, page = 1, pageSize = 10 }) {
    const q = new URLSearchParams({ propertyId, page, pageSize });
    if (asOf) q.set('asOf', asOf);
    return this.request(`/maintenance/pm/warranty-alerts?${q.toString()}`);
  }

  runPmCheck(body) {
    return this.request('/maintenance/pm/run', { method: 'POST', body });
  }

  /** Authorized photo fetch (the endpoint needs the Bearer token, so an <img src> cannot be used directly). */
  async getMaintenancePhotoBlob(incidentId, photoId) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/maintenance/incidents/${incidentId}/photos/${photoId}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`Photo failed to load (${res.status})`);
    return res.blob();
  }

  async getAssetLabelPdf({ propertyId, assetIds }) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/inwarding/tags/labels`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ propertyId, assetIds }),
    });
    if (!res.ok) {
      let message = `Label sheet failed (${res.status})`;
      try {
        message = (await res.json())?.error?.message || message;
      } catch {
        /* non-JSON error body */
      }
      throw new Error(message);
    }
    return res.blob();
  }

  async getTagBatchPdf(batchId) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/tags/batches/${batchId}/pdf`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) {
      let message = `Print sheet failed (${res.status})`;
      try {
        message = (await res.json())?.error?.message || message;
      } catch {
        /* non-JSON error body */
      }
      throw new Error(message);
    }
    return res.blob();
  }

  // ---- Finance, reports, billing and integrations gap closing (SRS v0.2) ----
  /** Downloads any API file (CSV / XLSX) with the stored token; returns { blob, filename }. */
  async downloadApiFile(path, fallbackName = 'export.csv') {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) {
      let message = `Download failed (${res.status})`;
      try {
        message = (await res.json())?.error?.message || message;
      } catch {
        /* non-JSON error body */
      }
      const err = new Error(message);
      err.status = res.status;
      throw err;
    }
    const disposition = res.headers.get('Content-Disposition') || '';
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] || fallbackName;
    return { blob: await res.blob(), filename };
  }

  qs(params) {
    const q = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const t = q.toString();
    return t ? `?${t}` : '';
  }

  getFinanceSettings(propertyId) {
    return this.request(`/book-policies/finance-settings${this.qs({ propertyId })}`);
  }

  saveFinanceSettings(body) {
    return this.request('/book-policies/finance-settings', { method: 'PUT', body });
  }

  getGlLayouts(propertyId) {
    return this.request(`/depreciation/gl-layouts${this.qs({ propertyId })}`);
  }

  saveGlLayout(target, { propertyId, columns }) {
    return this.request(`/depreciation/gl-layouts/${target}`, { method: 'PUT', body: { ...(propertyId ? { propertyId } : {}), columns } });
  }

  resetGlLayout(target, propertyId) {
    return this.request(`/depreciation/gl-layouts/${target}${this.qs({ propertyId })}`, { method: 'DELETE' });
  }

  downloadDisposalJournals({ propertyId, period, disposalId, target, format = 'csv' }) {
    return this.downloadApiFile(`/disposals/journals/export${this.qs({ propertyId, period, disposalId, target, format })}`, `disposal-journals.${format}`);
  }

  postDisposalToXero(id) {
    return this.request(`/disposals/${id}/xero`, { method: 'POST' });
  }

  listDisposalExports(id) {
    return this.request(`/disposals/${id}/exports`);
  }

  downloadDisposalScheduleFile({ propertyId, search, type, status, period, format = 'csv' }) {
    return this.downloadApiFile(`/disposals/export${this.qs({ propertyId, search, type, status, period, format })}`, `write-off-schedule.${format}`);
  }

  setRepairLifeExtending(id, lifeExtending) {
    return this.request(`/maintenance/repairs/${id}/life-extending`, { method: 'PUT', body: { lifeExtending } });
  }

  downloadRepairRecords({ propertyId, search, status, replacementOnly, format = 'csv' }) {
    return this.downloadApiFile(`/maintenance/repairs/export${this.qs({ propertyId, search, status, replacementOnly: replacementOnly ? 'true' : undefined, format })}`, `repair-records.${format}`);
  }

  downloadPmTasks({ propertyId, status, overdueOnly, search, format = 'csv' }) {
    return this.downloadApiFile(`/maintenance/pm/tasks/export${this.qs({ propertyId, status, overdueOnly: overdueOnly ? 'true' : undefined, search, format })}`, `pm-tasks.${format}`);
  }

  getOperationsDashboard({ propertyId } = {}) {
    return this.request(`/analytics/operations${this.qs({ propertyId })}`);
  }

  getFinanceDashboard({ propertyId, period } = {}) {
    return this.request(`/analytics/finance${this.qs({ propertyId, period })}`);
  }

  getLossBreakageReport(params = {}) {
    return this.request(`/analytics/loss-breakage${this.qs(params)}`);
  }

  downloadLossBreakage(params = {}) {
    return this.downloadApiFile(`/analytics/loss-breakage/export${this.qs(params)}`, `loss-breakage.${params.format || 'csv'}`);
  }

  listIntegrationOutboundEvents({ propertyId, status, page = 1, pageSize = 25 }) {
    return this.request(`/integrations/outbound/events${this.qs({ propertyId, status, page, pageSize })}`);
  }

  downloadIntegrationOutboundEvents({ propertyId, status, format = 'csv' }) {
    return this.downloadApiFile(`/integrations/outbound/events/export${this.qs({ propertyId, status, format })}`, `outbound-delivery-log.${format}`);
  }

  downloadIntegrationReviewQueue({ propertyId, status = 'pending', format = 'csv' }) {
    return this.downloadApiFile(`/integrations/review-queue/export${this.qs({ propertyId, status, format })}`, `integration-review-queue.${format}`);
  }

  resumeBlockedWork(propertyId) {
    return this.request(`/account-plans/properties/${propertyId}/resume-work`, { method: 'POST' });
  }

  // ---- Finance phase 2: impairment, components, book transfers, FX (AOS-FIN-013/014/015, AOS-MOV-008) ----
  searchBooks({ propertyId, search, page = 1, pageSize = 15 }) {
    return this.request(`/depreciation/books${this.qs({ propertyId, search, page, pageSize })}`);
  }

  getBookDetail(id) {
    return this.request(`/depreciation/books/${id}/detail`);
  }

  componentiseBook(id, body) {
    return this.request(`/depreciation/books/${id}/components`, { method: 'POST', body });
  }

  listImpairments({ propertyId, status, page = 1, pageSize = 25 }) {
    return this.request(`/depreciation/impairments${this.qs({ propertyId, status, page, pageSize })}`);
  }

  createImpairment(body) {
    return this.request('/depreciation/impairments', { method: 'POST', body });
  }

  approveImpairment(id) {
    return this.request(`/depreciation/impairments/${id}/approve`, { method: 'POST' });
  }

  rejectImpairment(id, reason) {
    return this.request(`/depreciation/impairments/${id}/reject`, { method: 'POST', body: { reason } });
  }

  listBookTransfers({ propertyId, page = 1, pageSize = 25 }) {
    return this.request(`/depreciation/book-transfers${this.qs({ propertyId, page, pageSize })}`);
  }

  applyBookTransferRate(id) {
    return this.request(`/depreciation/book-transfers/${id}/apply-fx-rate`, { method: 'POST' });
  }

  getFxSettings(propertyId) {
    return this.request(`/depreciation/fx${this.qs({ propertyId })}`);
  }

  setGroupCurrency({ propertyId, groupCurrency }) {
    return this.request('/depreciation/fx/group-currency', { method: 'PUT', body: { propertyId, groupCurrency } });
  }

  saveFxRate(body) {
    return this.request('/depreciation/fx/rates', { method: 'POST', body });
  }

  importFxRates({ propertyId, csv }) {
    return this.request('/depreciation/fx/rates/import', { method: 'POST', body: { propertyId, csv } });
  }

  deleteFxRate(id, propertyId) {
    return this.request(`/depreciation/fx/rates/${id}${this.qs({ propertyId })}`, { method: 'DELETE' });
  }

  // ---- Lifecycle and capex forecasts, occupancy and OE cost per occupied room (AOS-REP-007, AOS-RPT-006, AOS-STK-008, AOS-INT-005) ----
  getLifecycleForecast({ propertyId, groupBy = 'category', quarters = 8 } = {}) {
    return this.request(`/analytics/lifecycle-forecast${this.qs({ propertyId, groupBy, quarters })}`);
  }

  downloadLifecycleForecast({ propertyId, groupBy = 'category', format = 'csv' } = {}) {
    return this.downloadApiFile(`/analytics/lifecycle-forecast/export${this.qs({ propertyId, groupBy, format })}`, `lifecycle-forecast.${format}`);
  }

  getCapexForecast({ propertyId } = {}) {
    return this.request(`/analytics/capex-forecast${this.qs({ propertyId })}`);
  }

  draftCapexNarrative({ propertyId } = {}) {
    return this.request('/analytics/capex-forecast/narrative', { method: 'POST', body: propertyId ? { propertyId } : {} });
  }

  downloadCapexForecast({ propertyId, format = 'csv' } = {}) {
    return this.downloadApiFile(`/analytics/capex-forecast/export${this.qs({ propertyId, format })}`, `capex-forecast.${format}`);
  }

  getOccupancy({ propertyId, year } = {}) {
    return this.request(`/analytics/occupancy${this.qs({ propertyId, year })}`);
  }

  saveOccupancy({ propertyId, period, roomNights }) {
    return this.request('/analytics/occupancy', { method: 'PUT', body: { propertyId, period, roomNights } });
  }

  importOccupancyCsv({ propertyId, csv }) {
    return this.request('/integrations/imports/occupancy', { method: 'POST', body: propertyId ? { propertyId, csv } : { csv } });
  }

  getCostPerOccupiedRoom({ propertyId, from, to } = {}) {
    return this.request(`/analytics/cost-per-occupied-room${this.qs({ propertyId, from, to })}`);
  }

  downloadCostPerOccupiedRoom({ propertyId, from, to, format = 'csv' } = {}) {
    return this.downloadApiFile(`/analytics/cost-per-occupied-room/export${this.qs({ propertyId, from, to, format })}`, `oe-cost-per-occupied-room.${format}`);
  }

  // ---- Meter readings and meter-based preventive maintenance (AOS-REP-008) ----
  getAssetMeters(assetId) {
    return this.request(`/maintenance/meters/assets/${assetId}`);
  }

  recordMeterReading(assetId, body) {
    return this.request(`/maintenance/meters/assets/${assetId}/readings`, { method: 'POST', body });
  }
}

export default new ApiService();
